// Brand slice 1 (D157) — the full-bleed main screen, replacing the 560px home card. The front door of
// the Instant pillar: ONE primary action, two secondaries, a four-row how-to-play taste, and a quiet
// utility bar. Layout/pixels follow design_handoff_fafi_identity (1440×900 reference, responsive
// 1024→ultrawide); the 22-row controls list and the six drills moved off this screen (C3).
//
// House rules: injected providers only (T-ARCH-004) — bindings, name, session and teams all arrive as
// functions, so jsdom tests pin the contract; every string lands via textContent (§15.1). Key labels
// come from the LIVE bindings — never hardcoded (the mock's "Shift" tackle cap is overridden by C1).

import { keyLabel } from '../input/actionDefs';
import type { Bindings } from '../input/bindings';
import { createJerseyPreview, type JerseySpec } from './jerseyPreview';
import { keycap, link, primaryBtn, secondaryBtn, svgEl, svgIcon, wordmarkLockup } from './kit';

export interface HomeTeamSummary {
  readonly id: string;
  readonly name: string;
  readonly jersey: JerseySpec;
}

export interface HomeScreenOpts {
  doc: Document;
  /** LIVE bindings — re-read on every show() so a rebind reflects immediately. */
  bindings: () => Bindings;
  /** The "N new" training badge (computed: shipped lessons − completed). Hidden at 0. */
  newLessons: number;
  playerName: { get(): string; set(v: string): void };
  signedIn: () => boolean;
  /** Only called when signed in. */
  teams: () => Promise<HomeTeamSummary[]>;
  onPlay(): void;
  onCreateOnline(): void;
  onTraining(): void;
  onControls(): void;
  onSignIn(): void;
  onMyTeams(): void;
  /** D163: open the feedback box (overlays home). */
  onFeedback(): void;
}

export interface HomeScreen {
  readonly root: HTMLElement;
  /** (Re)render the dynamic parts — key labels, name, badge, auth zone — and show the screen. */
  show(): void;
  hide(): void;
}

/** The chalk pitch markings — one absolutely-positioned SVG, mostly below the fold, cropping from the
 *  edges as the viewport grows (`slice` against the 1440×900 reference). Flat and cheap, never a photo. */
function chalkMarkings(doc: Document): SVGSVGElement {
  const svg = svgEl<SVGSVGElement>(doc, 'svg', { viewBox: '0 0 1440 900', preserveAspectRatio: 'xMidYMax slice', fill: 'none' });
  svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%';
  svg.append(
    svgEl(doc, 'circle', { cx: 720, cy: 1050, r: 420, stroke: '#eef5ec', 'stroke-opacity': 0.07, 'stroke-width': 3 }),
    svgEl(doc, 'circle', { cx: 720, cy: 1050, r: 8, fill: '#eef5ec', 'fill-opacity': 0.07 }),
    svgEl(doc, 'line', { x1: 0, y1: 1050, x2: 1440, y2: 1050, stroke: '#eef5ec', 'stroke-opacity': 0.07, 'stroke-width': 3 }),
    svgEl(doc, 'path', { d: 'M 1440 60 A 90 90 0 0 0 1350 -30', stroke: '#eef5ec', 'stroke-opacity': 0.06, 'stroke-width': 3 }),
    svgEl(doc, 'path', { d: 'M 0 120 A 150 150 0 0 1 150 -30', stroke: '#eef5ec', 'stroke-opacity': 0.05, 'stroke-width': 3 }),
  );
  return svg;
}

export function mountHomeScreen(opts: HomeScreenOpts): HomeScreen {
  const d = opts.doc;
  const root = d.createElement('div');
  root.className = 'fafi-home';

  // background: page fill (class) + two floodlight glows + chalk markings
  const glow = d.createElement('div');
  glow.style.cssText =
    'position:absolute;inset:0;background:' +
    'radial-gradient(900px 480px at 18% -10%, rgba(255,178,36,0.07), transparent 65%),' +
    'radial-gradient(900px 480px at 85% -12%, rgba(255,178,36,0.05), transparent 65%)';
  root.append(glow, chalkMarkings(d));

  // ── content column ──────────────────────────────────────────────────────────
  const col = d.createElement('div');
  col.className = 'fafi-home-col';
  col.append(wordmarkLockup(d, 'centred'));

  // D164 (user call, overriding the handoff's §4.1 hierarchy): the ONLINE ROOM is the main play —
  // FAFI is a multiplayer game, and the kickabout is the warm-up. Create takes the primary weight;
  // the kickabout joins tier 2.
  const tier1 = d.createElement('div');
  tier1.className = 'fafi-home-tier1';
  tier1.append(primaryBtn(d, 'CREATE ONLINE ROOM', () => (hide(), opts.onCreateOnline())));
  const strap = d.createElement('div');
  strap.className = 'fafi-home-strap';
  strap.textContent = 'no sign-in, no download — kickoff in ten seconds';
  tier1.append(strap);

  const tier2 = d.createElement('div');
  tier2.className = 'fafi-home-tier2';
  tier2.append(secondaryBtn(d, 'PLAY KICKABOUT', () => (hide(), opts.onPlay())).el);
  const training = secondaryBtn(d, 'TRAINING', () => opts.onTraining(), opts.newLessons);
  tier2.append(training.el);

  // how-to-play taste: four rows only; the full list lives on the Controls screen + hold-H sheet
  const plate = d.createElement('div');
  plate.className = 'fafi-home-plate';
  const grid = d.createElement('div');
  grid.className = 'fafi-home-grid';
  const seeAll = link(d, 'See all controls →', () => opts.onControls());
  seeAll.classList.add('fafi-home-seeall');
  plate.append(grid, seeAll);
  col.append(tier1, tier2, plate);

  const rebuildStrip = (): void => {
    const b = opts.bindings();
    grid.replaceChildren();
    const row = (label: string, note: string | null, codes: readonly string[]): void => {
      const action = d.createElement('div');
      action.className = 'fafi-home-action';
      const name = d.createElement('span');
      name.textContent = label;
      action.append(name);
      if (note) {
        const n = d.createElement('span');
        n.className = 'fafi-home-note';
        n.textContent = ` (${note})`;
        action.append(n);
      }
      const keys = d.createElement('div');
      keys.className = 'fafi-home-keys';
      for (const code of codes) keys.append(keycap(d, keyLabel(code), { small: true }));
      grid.append(action, keys);
    };
    row('Move', null, [b.moveUp, b.moveLeft, b.moveDown, b.moveRight]);
    row('Shoot', 'hold to aim', [b.shoot]);
    row('Pass', null, [b.passDirect]);
    row('Tackle', 'safe, never a foul', [b.tackle]); // C1: the SAFE tackle's live key, not the mock's Shift
  };

  // ── utility bar ─────────────────────────────────────────────────────────────
  const bar = d.createElement('div');
  bar.className = 'fafi-utility';
  const left = d.createElement('div');
  left.className = 'fafi-utility-left';
  const right = d.createElement('div');
  right.className = 'fafi-utility-right';

  // identity chip — inline edit, not a labelled form field (D43 storage semantics unchanged)
  const chip = d.createElement('div');
  chip.className = 'fafi-chip';
  chip.tabIndex = 0;
  chip.setAttribute('role', 'button');
  chip.setAttribute('aria-label', 'Edit your display name');
  const nameSpan = d.createElement('span');
  nameSpan.style.cssText = 'font-size:14px;color:var(--ink-1)';
  chip.append(svgIcon(d, 'person', 14), nameSpan, svgIcon(d, 'pencil', 12));
  let editing = false;
  const beginEdit = (): void => {
    if (editing) return;
    editing = true;
    const input = d.createElement('input');
    input.maxLength = 20;
    input.value = opts.playerName.get();
    const done = (commit: boolean): void => {
      if (!editing) return;
      editing = false;
      if (commit) {
        const v = input.value.trim().slice(0, 20);
        if (v) opts.playerName.set(v); // empty ⇒ keep the old name
      }
      nameSpan.textContent = opts.playerName.get();
      input.replaceWith(nameSpan);
    };
    input.addEventListener('keydown', (e) => {
      // stopPropagation: a bubbling Enter/Escape would reach the chip's own keydown handler AFTER
      // done() clears `editing`, and instantly re-open the edit the user just committed.
      if (e.key === 'Enter') {
        e.stopPropagation();
        done(true);
      } else if (e.key === 'Escape') {
        e.stopPropagation();
        done(false);
      }
    });
    input.addEventListener('blur', () => done(true));
    nameSpan.replaceWith(input);
    input.focus();
    input.select();
  };
  chip.addEventListener('click', beginEdit);
  chip.addEventListener('keydown', (e) => {
    if (!editing && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      beginEdit();
    }
  });

  const auth = d.createElement('div');
  auth.className = 'fafi-home-auth';
  auth.style.cssText = 'display:flex;align-items:center;gap:10px';
  left.append(chip, auth);

  const feedbackLink = link(d, 'Feedback', () => opts.onFeedback(), { muted: true });
  feedbackLink.style.fontSize = '14px';
  const controlsLink = link(d, 'Controls', () => opts.onControls(), { muted: true });
  controlsLink.style.fontSize = '14px';
  right.append(feedbackLink, controlsLink); // Settings joins here when that screen exists (C2 — deliberately absent)
  bar.append(left, right);

  let authGen = 0; // sign-out during an in-flight teams fetch must not resurrect the crest row
  const rebuildAuth = (): void => {
    const gen = ++authGen;
    auth.replaceChildren();
    if (!opts.signedIn()) {
      const signIn = link(d, 'Sign in — keeps your teams, never required', () => opts.onSignIn(), { muted: true });
      signIn.style.fontSize = '14px';
      auth.append(signIn);
      return;
    }
    const label = d.createElement('span');
    label.style.cssText = 'font-size:13px;color:var(--ink-3)';
    label.textContent = 'Your teams:';
    const crests = d.createElement('span');
    crests.style.cssText = 'display:inline-flex;align-items:center;gap:6px';
    const myTeams = link(d, 'My teams', () => opts.onMyTeams());
    myTeams.style.fontSize = '13px';
    auth.append(label, crests, myTeams);
    opts
      .teams()
      .then((teams) => {
        if (gen !== authGen) return;
        for (const t of teams.slice(0, 5)) crests.append(createJerseyPreview(t.jersey, 26));
      })
      .catch(() => {
        /* the shelf stays empty; the My teams link still works */
      });
  };

  root.append(col, bar);
  d.body.appendChild(root);

  const show = (): void => {
    rebuildStrip();
    training.setBadge(opts.newLessons);
    nameSpan.textContent = opts.playerName.get();
    rebuildAuth();
    root.style.display = '';
  };
  const hide = (): void => {
    root.style.display = 'none';
  };
  return { root, show, hide };
}
